

import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject, catchError, distinctUntilChanged, filter, from, map, skip, switchMap, take, takeUntil, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ISentNotification } from '../interfaces/';
import { AuthService } from './auth.service';
import * as signalR from '@microsoft/signalr';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private refreshNotification = new BehaviorSubject<number>(0);
  private forwardMessage = new BehaviorSubject<string>('');
  forwardMessage$ = this.forwardMessage.asObservable().pipe(skip(1));
  refreshNotification$ = this.refreshNotification.pipe(
    switchMap((strategyId) => {
      if (strategyId) {
        return this.getNotifications(strategyId);
      } else {
        return EMPTY;
      }
    })
  );

  private token = '';
  private connection?: signalR.HubConnection;
  private startingPromise: Promise<void> | null = null;
  private readonly destroy$ = new Subject<void>();
  private domain = environment.domain;
  private isEnabled = environment.enableSignalR === true;

  constructor(private authService: AuthService, private http: HttpClient) {
    if (!this.isEnabled) {
      return;
    }
    this.authService.authBufferSub$.pipe(
      map((user) => user?.jwt || ''),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((jwt) => {
      this.token = jwt;
      if (!this.token) {
        // User logged out; stop any existing connection
        void this.stop();
        return;
      }
      // Token available/changed; ensure connection is running
      void this.start();
    });
  }

  private hubUrl(): string {
    // Use a relative URL so Angular dev-server proxy can forward it (including WebSockets).
    // Backend route: app.MapHub<NotificationHub>("/api/v1/ws")
    return `${environment.base_url}api/v1/ws`;
  }

  private buildConnection() {
    if (!this.isEnabled) {
      console.warn('SignalR disabled by configuration');
      return;
    }
    if (!this.token) {
      // Quietly skip until JWT exists to avoid noisy console warning at app boot
      return;
    }

    if (this.connection) return;

    const url = this.hubUrl();
    console.log('SignalR: building connection to', url);

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => this.token,
        // Allow fallback transports for debugging/proxy issues.
        // If WebSockets are blocked, SignalR can fall back to SSE/LongPolling.
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling
        // NOTE: do NOT set skipNegotiation=true unless you force WebSockets only.
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onreconnecting((error) => {
      console.warn('SignalR: reconnecting...', error);
    });

    this.connection.onreconnected((connectionId) => {
      console.log('SignalR: reconnected', connectionId);
    });

    this.connection.onclose((error) => {
      if (error) {
        console.error('Connection closed with error:', error);
      } else {
        console.log('Connection closed gracefully');
      }
    });

    // Handle server welcome callback (server may send 'welcomemessage' lowercase)
    this.connection.on('welcomemessage', (payload: any) => {
      console.log('Welcome message:', payload);
    });
    this.connection.on('WelcomeMessage', (payload: any) => {
      console.log('Welcome message:', payload);
    });

  }

  async start(): Promise<void> {
    if (!this.isEnabled) return;
    if (!this.token) return;

    this.buildConnection();
    if (!this.connection) return;

    if (this.connection.state === signalR.HubConnectionState.Connected) return;
    if (this.connection.state === signalR.HubConnectionState.Connecting) return;
    if (this.connection.state === signalR.HubConnectionState.Reconnecting) return;

    // Prevent parallel starts (common when multiple components subscribe at startup)
    if (this.startingPromise) return this.startingPromise;

    this.startingPromise = this.connection
      .start()
      .then(() => {
        console.log('SignalR: connected');
      })
      .catch((err) => {
        console.error('SignalR: failed to start', err);
        throw err;
      })
      .finally(() => {
        this.startingPromise = null;
      });

    return this.startingPromise;
  }

  async stop(): Promise<void> {
    if (!this.connection) return;
    try {
      await this.connection.stop();
    } finally {
      this.connection = undefined;
      this.startingPromise = null;
    }
  }

  // Legacy API (kept to avoid breaking callers)
  connect(): Observable<void> {
    if (!this.isEnabled) {
      return EMPTY;
    }
    return from(this.start()).pipe(catchError(() => EMPTY));
  }

  public getConnectionState(): signalR.HubConnectionState {
    if (!this.connection) {
      return signalR.HubConnectionState.Disconnected;
    }
    return this.connection.state;
  }

  public reconnectIfDisconnected(): void {
    if (!this.connection) {
      return; // Silently return if not initialized
    }
    console.log("this.connection.state", this.connection.state);
    if (this.connection.state === signalR.HubConnectionState.Disconnected) {
      this.connect();
    }
  }

  sendMessage(message: Partial<ISentNotification>, isServerSide = false): Observable<void> {
    if (!isServerSide) {
      if (!this.isEnabled || !this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
        return EMPTY; // Silently skip if not connected
      }
      return from(this.connection.invoke('SendMessage', message)).pipe(tap(x => console.log("sending....")));
    }
    return EMPTY;
  }

  joinProject(projectId: number): Observable<void> {
    if (!this.isEnabled) {
      return EMPTY;
    }
    
    // If not connected, wait and retry
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return new Observable(observer => {
        const maxRetries = 10;
        let retryCount = 0;
        
        const checkConnection = setInterval(() => {
          if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
            clearInterval(checkConnection);
            console.log("#projectId#", projectId);
            from(this.connection.invoke('JoinProject', projectId.toString())).subscribe({
              next: () => observer.complete(),
              error: (err) => observer.error(err)
            });
          } else if (retryCount >= maxRetries) {
            clearInterval(checkConnection);
            observer.complete(); // Complete silently after retries
          }
          retryCount++;
        }, 500); // Check every 500ms
      });
    }
    
    console.log("#projectId#", projectId);
    return from(this.connection.invoke('JoinProject', projectId.toString()));
  }

  leaveProject(projectId: number): Observable<void> {
    if (!this.isEnabled || !this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return EMPTY; // Silently skip if not connected
    }
    console.log("#LeaveProject#", projectId);
    return from(this.connection.invoke('LeaveProject', projectId.toString()));
  }

  sendMessageToProject(message: any): Observable<void> {
    if (!this.isEnabled || !this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return EMPTY; // Silently skip if not connected
    }
    console.log("#SendMessageToProject#", message);
    return from(this.connection.invoke('SendMessageToProject', message));
  }

  listen(event: string): Observable<any> {
    if (!this.isEnabled) { return EMPTY; }
    return new Observable<any>((observer) => {
      const conn = this.connection;
      if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
        observer.complete();
        return () => {};
      }
      const handler = (message: any) => {
        console.log("listen message", message);
        observer.next(message);
      };
      conn.on(event, handler);
      return () => {
        conn.off(event, handler);
      };
    });
  }

  getMessages(): Observable<any> {
    if (!this.isEnabled) { return EMPTY; }
    return new Observable<any>((observer) => {
      const conn = this.connection;
      if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
        observer.complete();
        return () => {};
      }
      const handler = (data: any) => {
        console.log("getMessages message", data);
        
        // Send delivery acknowledgment back to server
        this.sendDeliveryAck(data.id).subscribe({
          next: () => console.log(`Delivery acknowledgment sent for notification ${data.id}`),
          error: (err) => console.error(`Failed to send delivery acknowledgment:`, err)
        });
        
        observer.next(data);
      };
      conn.on('message', handler);
      return () => {
        conn.off('message', handler);
      };
    });
  }

  sendDeliveryAck(notificationId: number): Observable<void> {
    if (!this.isEnabled || !this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return EMPTY;
    }
    return from(this.connection.invoke('ReceiveAcknowledgment', notificationId));
  }

  testWekcomeMessage(): Observable<any> {
    if (!this.isEnabled) { return EMPTY; }
    return new Observable<any>((observer) => {
      const conn = this.connection;
      if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
        observer.complete();
        return () => {};
      }
      const handler = (data: any) => {
        observer.next(data);
      };
      conn.on('WelcomeMessage', handler);
      return () => {
        conn.off('WelcomeMessage', handler);
      };
    });
  }

  listenToProjectMessages(): Observable<any> {
    if (!this.isEnabled) { return EMPTY; }
    return new Observable<any>((observer) => {
      const conn = this.connection;
      if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
        observer.complete();
        return () => {};
      }
      const handler = (data: any) => {
        observer.next(data);
      };
      conn.on('receiveProjectMessage', handler);
      return () => {
        conn.off('receiveProjectMessage', handler);
      };
    });
  }

  receiveReminderMessage(): Observable<any> {
    if (!this.isEnabled) { return EMPTY; }
    return new Observable<any>((observer) => {
      const conn = this.connection;
      if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
        observer.complete();
        return () => {};
      }
      const handler = (data: any) => {
        observer.next(data);
      };
      conn.on('ReceiveReminder', handler);
      conn.on('receivereminder', handler);
      return () => {
        conn.off('ReceiveReminder', handler);
        conn.off('receivereminder', handler);
      };
    });
  }

  getNotifications(strategyId: number): Observable<any> {
    return this.http.get(`${this.domain}user/notifications?strategyId=${strategyId}`).pipe(take(1));
  }

  setRefreshNotification(strategyId: number) {
    this.refreshNotification.next(strategyId);
  }

  forwardMessageFn(message: any) {
    this.forwardMessage.next(message);
  }
}
